import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { etichettaPiano, variantePiano, formatDataOra, formatData, formatDurata } from '../../lib/format'
import pagina from '../pagine.module.css'

interface DettaglioTeam {
  team: { id: string; nome: string; piano: string; attivo: boolean; maxMembri: number; createdAt: string; noteSuperadmin?: string | null }
  membri: Array<{ id: string; telefono: string; nome_display: string | null; attivo: boolean }>
  sessioni_recenti: Array<{ id: string; durataSecondi: number; votoComplessivo: number | null; createdAt: string }>
}

export function AdminTeamDettaglio() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const q = useQuery({
    queryKey: ['admin', 'team', id],
    queryFn: async () => (await api.get<{ dati: DettaglioTeam }>(`/superadmin/teams/${id}`)).data.dati,
    enabled: !!id,
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Team non trovato" />

  const t = q.data.team
  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <Button variante="ghost" taglia="sm" iconaSinistra={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
          Indietro
        </Button>
      </div>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>{t.nome}</h2>
          <Badge variante={variantePiano(t.piano)}>{etichettaPiano(t.piano)}</Badge>
          {t.attivo
            ? <Badge variante="success">Attivo</Badge>
            : <Badge variante="warning">Sospeso</Badge>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Creato {formatData(t.createdAt)} • Max {t.maxMembri} membri • {q.data.membri.length} membri attuali
        </div>
        {t.noteSuperadmin && (
          <div style={{
            marginTop: 14, padding: 12, background: 'var(--warning-soft)', color: 'var(--warning)',
            borderRadius: 'var(--radius-md)', fontSize: 13,
          }}>
            <strong>Note interne:</strong> {t.noteSuperadmin}
          </div>
        )}
      </Card>

      <div className={pagina.due}>
        <Card titolo={`Membri (${q.data.membri.length})`}>
          {q.data.membri.length === 0 ? (
            <EmptyState titolo="Nessun membro" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {q.data.membri.map((m) => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: 10, background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)',
                }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{m.nome_display ?? 'Senza nome'}</div>
                    <div className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.telefono}</div>
                  </div>
                  {m.attivo
                    ? <Badge variante="success">Attivo</Badge>
                    : <Badge variante="danger">Sospeso</Badge>}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card titolo={`Sessioni recenti (${q.data.sessioni_recenti.length})`}>
          {q.data.sessioni_recenti.length === 0 ? (
            <EmptyState titolo="Nessuna sessione" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {q.data.sessioni_recenti.slice(0, 10).map((s) => (
                <div
                  key={s.id}
                  onClick={() => navigate(`/app/sessioni/${s.id}`)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: 10, background: 'var(--bg-subtle)',
                    borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{formatDataOra(s.createdAt)}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span className="mono" style={{ fontSize: 13 }}>{formatDurata(s.durataSecondi)}</span>
                    {s.votoComplessivo != null && (
                      <span className="mono" style={{ fontWeight: 600 }}>{s.votoComplessivo}/10</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
